(() => {
  const SELECTOR = '.ht2-account';


  const escapeHtml = (value = '') => String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const compact = (values) => values.filter((value) => value != null && String(value).trim() !== '');

  const flattenValidationErrors = (errors = {}) => Object.values(errors)
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .filter(Boolean)
    .map(String);

  const responseErrorMessage = (payload, fallback) => {
    if (Array.isArray(payload?.errors)) {
      const messages = payload.errors
        .map((error) => error?.message || error)
        .filter(Boolean)
        .map(String);
      if (messages.length) return messages.join(', ');
    }

    const validationErrors = payload?.errors && typeof payload.errors === 'object'
      ? flattenValidationErrors(payload.errors)
      : [];
    if (validationErrors.length) return validationErrors.join(', ');

    return payload?.message || payload?.error || fallback;
  };

  const numericResourceId = (value) => {
    const match = String(value || '').match(/\d+/);
    return match ? match[0] : '';
  };

  const sameResourceId = (left, right) => {
    const leftId = numericResourceId(left);
    const rightId = numericResourceId(right);
    return leftId && rightId ? leftId === rightId : String(left || '') === String(right || '');
  };

  const pathResourceId = (value) => {
    const id = numericResourceId(value) || String(value || '').trim();
    return encodeURIComponent(id);
  };

  const toCustomerGid = (value) => {
    const id = String(value || '').trim();
    if (!id) return '';
    if (id.startsWith('gid://shopify/Customer/')) return id;
    const numeric = numericResourceId(id);
    return numeric ? `gid://shopify/Customer/${numeric}` : id;
  };

  const toCustomerAddressGid = (value) => {
    const id = String(value || '').trim();
    if (!id) return '';
    if (id.startsWith('gid://shopify/MailingAddress/')) return id;
    const numeric = numericResourceId(id);
    return numeric
      ? `gid://shopify/MailingAddress/${numeric}?model_name=CustomerAddress`
      : id;
  };

  const normalizeAddress = (address = {}, isDefault = false) => ({
    id: address.id || address.addressId || address.address_id || '',
    firstName: address.firstName || address.first_name || '',
    lastName: address.lastName || address.last_name || '',
    company: address.company || '',
    address1: address.address1 || '',
    address2: address.address2 || '',
    city: address.city || '',
    province: address.province || '',
    provinceCode: address.provinceCode || address.province_code || '',
    zip: address.zip || '',
    country: address.country || '',
    countryCode: address.countryCodeV2 || address.countryCode || address.country_code || '',
    phone: address.phone || '',
    isDefault: Boolean(address.isDefault ?? address.is_default ?? isDefault),
  });

  const formatDate = (value, options = {}) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      ...options,
    }).format(date);
  };

  const statusKey = (order) => {
    if (order.cancelled) return 'cancelled';
    const status = String(order.fulfillmentStatus || '').toLowerCase();
    if (status === 'fulfilled') return 'fulfilled';
    return 'active';
  };

  const statusLabel = (order) => {
    if (order.cancelled) return 'Cancelled';
    return order.fulfillmentStatusLabel || (statusKey(order) === 'fulfilled' ? 'Delivered' : 'Preparing');
  };

  const addressLines = (address = {}) => compact([
    [address.firstName, address.lastName].filter(Boolean).join(' '),
    address.company,
    address.address1,
    address.address2,
    compact([address.city, address.province, address.zip]).join(', '),
    address.country,
    address.phone,
  ]);


  const normalizePropertyKey = (value = '') => String(value)
    .replace(/^_+/, '')
    .replace(/[^a-z0-9]/gi, '')
    .toLowerCase();

  const lineItemProperties = (item = {}) => {
    if (Array.isArray(item.properties)) {
      return item.properties
        .map((property) => ({
          name: property?.name ?? property?.key ?? property?.[0] ?? '',
          value: property?.value ?? property?.[1] ?? '',
        }))
        .filter((property) => property.name && property.value != null && String(property.value).trim() !== '');
    }

    if (item.properties && typeof item.properties === 'object') {
      return Object.entries(item.properties)
        .map(([name, value]) => ({ name, value }))
        .filter((property) => property.value != null && String(property.value).trim() !== '');
    }

    const attributeSource = Array.isArray(item.customAttributes)
      ? item.customAttributes
      : Array.isArray(item.attributes)
        ? item.attributes
        : null;

    if (attributeSource) {
      return attributeSource
        .map((property) => ({ name: property?.name ?? property?.key ?? '', value: property?.value ?? '' }))
        .filter((property) => property.name && property.value != null && String(property.value).trim() !== '');
    }

    return [];
  };

  const lineItemProperty = (item, names) => {
    const accepted = new Set(names.map(normalizePropertyKey));
    const property = lineItemProperties(item).find((entry) => accepted.has(normalizePropertyKey(entry.name)));
    return property ? String(property.value).trim() : '';
  };

  const lineItemClassification = (item) => ({
    dayDate: lineItemProperty(item, ['DayDate', 'Day Date', 'DeliveryDate', 'Delivery Date']) || 'Date not specified',
    type: lineItemProperty(item, ['Type']) || 'Type not specified',
    mainType: lineItemProperty(item, ['_MainType', 'MainType', 'Main Type']) || 'Main type not specified',
  });

  const formatScheduleDate = (value) => {
    const raw = String(value || '').trim();
    if (!raw || raw === 'Date not specified') return raw || 'Date not specified';

    const isoDate = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? `${raw}T00:00:00` : raw;
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) return raw;

    return new Intl.DateTimeFormat(undefined, {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  };

  const normaliseTracking = (tracking = {}) => {
    const trackingNumbers = Array.isArray(tracking.trackingNumbers)
      ? tracking.trackingNumbers
      : Array.isArray(tracking.tracking_numbers)
        ? tracking.tracking_numbers
        : [];

    const number = String(
      tracking.trackingNumber
      || tracking.tracking_number
      || tracking.number
      || trackingNumbers[0]
      || ''
    ).trim();

    return {
      createdAt: tracking.createdAt || tracking.created_at || '',
      company: String(tracking.trackingCompany || tracking.tracking_company || tracking.company || '').trim(),
      number,
      numbers: trackingNumbers.map(String).filter(Boolean),
      url: String(tracking.trackingUrl || tracking.tracking_url || tracking.url || '').trim(),
      status: String(tracking.status || tracking.fulfillmentStatus || tracking.fulfillment_status || '').trim(),
    };
  };

  const trackingFromLineItemProperties = (item = {}) => {
    const number = lineItemProperty(item, [
      '_TrackingNumber', 'TrackingNumber', 'Tracking Number', 'tracking_number'
    ]);
    const url = lineItemProperty(item, [
      '_TrackingUrl', '_TrackingURL', 'TrackingUrl', 'Tracking URL', 'tracking_url'
    ]);
    const company = lineItemProperty(item, [
      '_TrackingCompany', 'TrackingCompany', 'Tracking Company', 'Carrier', 'Courier'
    ]);
    const status = lineItemProperty(item, [
      '_TrackingStatus', 'TrackingStatus', 'Tracking Status', '_DeliveryStatus', 'DeliveryStatus', 'Delivery Status'
    ]);

    if (!number && !url && !company && !status) return null;
    return normaliseTracking({ number, url, company, status });
  };

  const lineItemTrackings = (item = {}) => {
    const values = [];

    if (Array.isArray(item.fulfillments)) {
      item.fulfillments.forEach((fulfillment) => values.push(normaliseTracking(fulfillment)));
    } else if (item.fulfillment) {
      values.push(normaliseTracking(item.fulfillment));
    }

    const propertyTracking = trackingFromLineItemProperties(item);
    if (propertyTracking) values.push(propertyTracking);

    return values.filter((tracking) => (
      tracking.number || tracking.url || tracking.company || tracking.status || tracking.createdAt
    ));
  };

  const deliveryTrackingList = (items = []) => {
    const seen = new Set();
    const trackings = [];

    items.flatMap(lineItemTrackings).forEach((tracking) => {
      const key = [tracking.number, tracking.url, tracking.company, tracking.status]
        .map((value) => String(value || '').trim().toLowerCase())
        .join('|');
      if (!key.replaceAll('|', '') || seen.has(key)) return;
      seen.add(key);
      trackings.push(tracking);
    });

    return trackings;
  };

  const deliveryGroupKey = ({ dayDate, type, mainType }) => [dayDate, type, mainType].join('|||');

  const deliveryStatus = (delivery = {}) => {
    const order = delivery.parentOrder || {};
    const rawStatus = String(
      delivery.statusOverride
      || delivery.trackings?.find((tracking) => tracking.status)?.status
      || ''
    ).trim();
    const normalised = rawStatus.toLowerCase().replaceAll('_', ' ');

    if (order.cancelled || normalised.includes('cancel')) {
      return { key: 'cancelled', label: 'Cancelled', progressIndex: 0 };
    }

    if (normalised.includes('deliver') || normalised.includes('complete')) {
      return { key: 'fulfilled', label: rawStatus || 'Delivered', progressIndex: 4 };
    }

    if (normalised.includes('out for delivery')) {
      return { key: 'active', label: rawStatus || 'Out for delivery', progressIndex: 3 };
    }

    if (normalised.includes('transit')) {
      return { key: 'active', label: rawStatus || 'In transit', progressIndex: 3 };
    }

    if (normalised.includes('ship') || normalised.includes('dispatch') || normalised.includes('fulfill')) {
      return { key: 'active', label: rawStatus || 'Dispatched', progressIndex: 3 };
    }

    if (normalised.includes('pack')) {
      return { key: 'active', label: rawStatus || 'Packed', progressIndex: 2 };
    }

    if (normalised.includes('confirm')) {
      return { key: 'active', label: rawStatus || 'Confirmed', progressIndex: 1 };
    }

    if (delivery.trackings?.some((tracking) => tracking.number || tracking.url)) {
      return { key: 'active', label: 'In transit', progressIndex: 3 };
    }

    if (delivery.hasFulfillment) {
      return { key: 'active', label: 'Fulfilled', progressIndex: 3 };
    }

    const parentStatus = String(order.fulfillmentStatus || '').toLowerCase();
    if (parentStatus === 'fulfilled' || parentStatus === 'complete') {
      return { key: 'fulfilled', label: 'Delivered', progressIndex: 4 };
    }

    return { key: 'active', label: 'Preparing', progressIndex: 2 };
  };

  const formatMoneyFromSubunits = (value, currency = 'GBP') => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '';

    try {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: String(currency || 'GBP').toUpperCase(),
      }).format(amount / 100);
    } catch {
      return (amount / 100).toFixed(2);
    }
  };

  const buildDeliveryOrders = (order = {}) => {
    const groups = new Map();
    const items = Array.isArray(order.lineItems) ? order.lineItems : [];

    items.forEach((item) => {
      const classification = lineItemClassification(item);
      const key = deliveryGroupKey(classification);

      if (!groups.has(key)) {
        groups.set(key, {
          key,
          dayDate: classification.dayDate,
          dayLabel: formatScheduleDate(classification.dayDate),
          type: classification.type,
          mainType: classification.mainType,
          items: [],
          quantity: 0,
          lineTotalSubunits: 0,
          hasPriceSubunits: false,
        });
      }

      const delivery = groups.get(key);
      delivery.items.push(item);
      delivery.quantity += Math.max(1, Number(item.quantity || 1));

      const lineTotal = Number(item.finalLinePriceSubunits ?? item.linePriceSubunits);
      if (Number.isFinite(lineTotal)) {
        delivery.lineTotalSubunits += lineTotal;
        delivery.hasPriceSubunits = true;
      }
    });

    const deliveries = Array.from(groups.values()).sort((left, right) => {
      const leftTime = Date.parse(left.dayDate);
      const rightTime = Date.parse(right.dayDate);
      if (!Number.isNaN(leftTime) && !Number.isNaN(rightTime) && leftTime !== rightTime) {
        return leftTime - rightTime;
      }
      if (Number.isNaN(leftTime) !== Number.isNaN(rightTime)) return Number.isNaN(leftTime) ? 1 : -1;
      const typeCompare = left.type.localeCompare(right.type);
      return typeCompare || left.mainType.localeCompare(right.mainType);
    });

    return deliveries.map((delivery, index) => {
      const trackings = deliveryTrackingList(delivery.items);
      const statusOverride = delivery.items.find((item) => item.deliveryStatusOverride)?.deliveryStatusOverride || '';
      const hasFulfillment = delivery.items.some((item) => (
        item.fulfillment || (Array.isArray(item.fulfillments) && item.fulfillments.length)
      ));
      const total = delivery.hasPriceSubunits
        ? formatMoneyFromSubunits(delivery.lineTotalSubunits, order.currency)
        : '';

      const built = {
        ...delivery,
        parentOrder: order,
        orderId: order.id,
        orderName: order.name,
        orderCreatedAt: order.createdAt,
        sequence: index + 1,
        deliveryCount: deliveries.length,
        reference: `${order.name || 'Order'}-D${String(index + 1).padStart(2, '0')}`,
        trackings,
        hasFulfillment,
        statusOverride,
        total,
      };

      built.status = deliveryStatus(built);
      return built;
    });
  };

  const buildAllDeliveryOrders = (orders = []) => orders.flatMap((order) => buildDeliveryOrders(order));

  const mergeRemoteOrders = (remoteOrders = [], localOrders = []) => remoteOrders.map((remoteOrder) => {
    const localOrder = localOrders.find((order) => sameResourceId(order.id, remoteOrder.id) || String(order.name || '') === String(remoteOrder.name || ''));
    if (!localOrder) return remoteOrder;

    const remoteItems = Array.isArray(remoteOrder.lineItems) ? remoteOrder.lineItems : [];
    const localItems = Array.isArray(localOrder.lineItems) ? localOrder.lineItems : [];
    const mergedItems = remoteItems.map((remoteItem) => {
      const localItem = localItems.find((item) => sameResourceId(item.id, remoteItem.id)
        || (String(item.variantId || '') && sameResourceId(item.variantId, remoteItem.variantId))
        || String(item.title || '') === String(remoteItem.title || ''));
      if (!localItem) return remoteItem;
      return {
        ...localItem,
        ...remoteItem,
        properties: lineItemProperties(remoteItem).length ? remoteItem.properties : localItem.properties,
      };
    });

    return {
      ...localOrder,
      ...remoteOrder,
      lineItems: mergedItems.length ? mergedItems : localItems,
    };
  });

  const normalizeState = (state = {}) => ({
    customer: state.customer || {},
    addresses: Array.isArray(state.addresses) ? state.addresses : [],
    orders: Array.isArray(state.orders) ? state.orders : [],
    rewards: state.rewards || null,
    favorites: Array.isArray(state.favorites) ? state.favorites : [],
    referral: state.referral || null,
    reviews: {
      pending: Array.isArray(state.reviews?.pending) ? state.reviews.pending : [],
      submitted: Array.isArray(state.reviews?.submitted) ? state.reviews.submitted : [],
    },
    preferences: state.preferences || {},
    capabilities: state.capabilities || {},
  });

  class CustomerPortal {
    constructor(root) {
      this.root = root;
      this.initialState = this.readInitialState();
      this.apiBase = String(root.dataset.apiBase || '').trim();
      this.customerApiBase = String(
        root.dataset.customerApiBase || 'https://healthytiffin.app/api'
      ).trim();
      // Optional only. Never place a private API key in theme markup.
      this.customerApiKey = String(root.dataset.customerApiKey || '').trim();
      this.customerApiCredentials = String(root.dataset.customerApiCredentials || 'omit').trim();
      this.customerId = String(root.dataset.customerId || this.initialState?.customer?.id || '').trim();
      this.designMode = root.dataset.designMode === 'true';
      this.state = normalizeState(this.initialState);
      this.orderFilter = 'all';
      this.activePanel = 'dashboard';
      this.lastFocusedElement = null;
      this.documentKeydownHandler = null;
      this.init();
    }

    readInitialState() {
      const node = this.root.querySelector('[data-ht2-initial-state]');
      if (!node) return {};
      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        console.error('Unable to parse customer account state', error);
        return {};
      }
    }

    async init() {
      if (this.designMode) this.bindDesignModeGuards();
      this.bindNavigation();
      this.bindModals();
      this.bindForms();
      this.bindActions();
      this.setCurrentDate();
      this.renderAll();
      if (!this.designMode && this.apiBase) await this.refreshFromApp();
    }

    bindDesignModeGuards() {
      this.root.addEventListener('submit', (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.toast('Theme editor preview only. No customer data was changed.');
      }, true);

      this.root.addEventListener('click', (event) => {
        const guardedAction = event.target.closest([
          '[data-address-default]',
          '[data-address-delete]',
          '[data-delivery-reorder]',
          '[data-delivery-cancel]',
          '[data-favorite-remove]',
          '[data-favorite-add]'
        ].join(','));
        if (!guardedAction) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.toast('Theme editor preview only. This action is disabled.');
      }, true);
    }

    bindNavigation() {
      this.root.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-panel-target]');
        if (!trigger || !this.root.contains(trigger)) return;
        const panelName = trigger.dataset.panelTarget;
        if (!panelName) return;
        event.preventDefault();
        this.showPanel(panelName);
      });

      const hashPanel = window.location.hash.replace('#account-', '');
      if (hashPanel && this.root.querySelector(`[data-panel="${CSS.escape(hashPanel)}"]`)) {
        this.showPanel(hashPanel, false);
      }
    }

    showPanel(panelName, updateUrl = true) {
      const panel = this.root.querySelector(`[data-panel="${CSS.escape(panelName)}"]`);
      if (!panel) return;
      this.activePanel = panelName;
      this.root.querySelectorAll('[data-panel]').forEach((item) => item.classList.toggle('is-active', item === panel));
      this.root.querySelectorAll('[data-panel-target]').forEach((button) => {
        const active = button.dataset.panelTarget === panelName;
        button.classList.toggle('is-active', active);
        if (button.matches('.ht2-nav-item')) {
          if (active) button.setAttribute('aria-current', 'page');
          else button.removeAttribute('aria-current');
        }
      });
      if (updateUrl) history.replaceState(null, '', `#account-${panelName}`);

      const heading = panel.querySelector('h1');
      if (heading) {
        if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
      }

      const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({
        top: Math.max(this.root.getBoundingClientRect().top + window.scrollY - 20, 0),
        behavior: reducedMotion ? 'auto' : 'smooth',
      });
    }

    bindModals() {
      this.root.addEventListener('click', (event) => {
        const close = event.target.closest('[data-modal-close]');
        if (close) this.closeModal(close.closest('[data-modal]'));

        const drawerClose = event.target.closest('[data-order-drawer-close]');
        if (drawerClose) this.closeOrderDrawer();
      });

      this.documentKeydownHandler = (event) => {
        if (event.key !== 'Escape') return;
        this.root.querySelectorAll('[data-modal]:not([hidden])').forEach((modal) => this.closeModal(modal));
        if (!this.root.querySelector('[data-order-drawer]')?.hidden) this.closeOrderDrawer();
      };

      document.addEventListener('keydown', this.documentKeydownHandler);
    }

    openModal(name) {
      const modal = this.root.querySelector(`[data-modal="${CSS.escape(name)}"]`);
      if (!modal) return null;
      this.lastFocusedElement = document.activeElement;
      modal.hidden = false;
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(() => modal.querySelector('input:not([type="hidden"]), select, textarea, button')?.focus());
      return modal;
    }

    closeModal(modal) {
      if (!modal) return;
      modal.hidden = true;
      if (![...this.root.querySelectorAll('[data-modal]')].some((item) => !item.hidden)) document.body.style.overflow = '';
      this.lastFocusedElement?.focus?.();
    }

    bindForms() {
      this.root.querySelector('[data-profile-form]')?.addEventListener('submit', (event) => this.submitProfile(event));
      const addressForm = this.root.querySelector('[data-address-form]');
      addressForm?.addEventListener('submit', (event) => this.submitAddress(event));
      this.root.querySelector('[data-cancel-form]')?.addEventListener('submit', (event) => this.submitCancellation(event));
      this.root.querySelector('[data-edit-request-form]')?.addEventListener('submit', (event) => this.submitEditRequest(event));
      this.root.querySelector('[data-review-form]')?.addEventListener('submit', (event) => this.submitReview(event));
      this.root.querySelector('[data-preferences-form]')?.addEventListener('submit', (event) => this.submitPreferences(event));
    }

    bindActions() {
      this.root.addEventListener('click', (event) => {
        const createAddress = event.target.closest('[data-address-create]');
        if (createAddress) this.openAddressModal();

        const editAddress = event.target.closest('[data-address-edit]');
        if (editAddress) this.openAddressModal(this.findAddress(editAddress.dataset.addressEdit));

        const defaultAddress = event.target.closest('[data-address-default]');
        if (defaultAddress) this.setDefaultAddress(defaultAddress.dataset.addressDefault, defaultAddress);

        const deleteAddress = event.target.closest('[data-address-delete]');
        if (deleteAddress) this.deleteAddress(deleteAddress.dataset.addressDelete, deleteAddress);

        const detail = event.target.closest('[data-delivery-detail]');
        if (detail) {
          const result = this.findDelivery(detail.dataset.orderId, detail.dataset.deliveryKey);
          if (result) this.openOrderDrawer(result.order, result.delivery);
        }

        const reorder = event.target.closest('[data-delivery-reorder]');
        if (reorder) {
          const result = this.findDelivery(reorder.dataset.orderId, reorder.dataset.deliveryKey);
          if (result) this.reorder({ lineItems: result.delivery.items }, reorder);
        }

        const cancel = event.target.closest('[data-delivery-cancel]');
        if (cancel) {
          const result = this.findDelivery(cancel.dataset.orderId, cancel.dataset.deliveryKey);
          if (result) this.openCancellation(result.order, result.delivery);
        }

        const editRequest = event.target.closest('[data-delivery-edit-request]');
        if (editRequest) {
          const result = this.findDelivery(editRequest.dataset.orderId, editRequest.dataset.deliveryKey);
          if (result) this.openEditRequest(result.order, result.delivery);
        }

        const favoriteRemove = event.target.closest('[data-favorite-remove]');
        if (favoriteRemove) this.removeFavorite(favoriteRemove.dataset.favoriteRemove);

        const favoriteAdd = event.target.closest('[data-favorite-add]');
        if (favoriteAdd) this.addVariantToCart(favoriteAdd.dataset.favoriteAdd, 1, favoriteAdd);

        const referralCopy = event.target.closest('[data-referral-copy]');
        if (referralCopy) this.copyReferral(referralCopy);

        const reviewOpen = event.target.closest('[data-review-open]');
        if (reviewOpen) this.openReview(reviewOpen.dataset.reviewOpen);

        const rating = event.target.closest('[data-rating-value]');
        if (rating) this.setRating(Number(rating.dataset.ratingValue));

        const filter = event.target.closest('[data-order-filter]');
        if (filter) {
          this.orderFilter = filter.dataset.orderFilter || 'all';
          this.root.querySelectorAll('[data-order-filter]').forEach((item) => item.classList.toggle('is-active', item === filter));
          this.renderOrders();
        }
      });
    }

    customerApiUrl(path = '') {
      if (!this.customerApiBase) {
        throw new Error('The Shopify customer API URL has not been configured.');
      }

      const baseUrl = new URL(this.customerApiBase, window.location.origin);
      baseUrl.pathname = `${baseUrl.pathname.replace(/\/+$/, '')}/`;

      const cleanPath = String(path || '').replace(/^\/+/, '');
      const url = new URL(cleanPath, baseUrl);

      if (!['http:', 'https:'].includes(url.protocol)) {
        throw new Error('The Shopify customer API URL must use HTTP or HTTPS.');
      }

      return url;
    }

    async customerApi(path, options = {}) {
      if (!this.customerId) {
        throw new Error('The logged-in Shopify customer could not be identified.');
      }

      const url = this.customerApiUrl(path);
      const sameOrigin = url.origin === window.location.origin;
      const headers = {
        Accept: 'application/json',
        ...(options.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(options.headers || {}),
      };

      // Only use this for a browser-safe, restricted public key. A private key
      // must be added by a server-side proxy and never exposed in theme code.
      if (this.customerApiKey) headers['X-API-KEY'] = this.customerApiKey;

      if (sameOrigin) {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
        if (csrfToken) headers['X-CSRF-TOKEN'] = csrfToken;
      }

      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 20000);

      const method = options.method || 'GET';
      const credentials = options.credentials || (
        sameOrigin
          ? 'same-origin'
          : this.customerApiCredentials === 'include'
            ? 'include'
            : 'omit'
      );

      let response;
      try {
        response = await fetch(url.toString(), {
          method,
          mode: sameOrigin ? 'same-origin' : 'cors',
          credentials,
          cache: 'no-store',
          redirect: 'follow',
          headers,
          signal: controller.signal,
          body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
        });
      } catch (error) {
        console.error('[Healthy Tiffin] Customer API network failure', {
          method,
          url: url.toString(),
          error,
        });

        if (error?.name === 'AbortError') {
          throw new Error('The Shopify customer service timed out. Please try again.');
        }

        throw new Error(
          `The customer API did not return a valid response. Check DNS, SSL, CORS and OPTIONS for ${url.origin}.`
        );
      } finally {
        window.clearTimeout(timeout);
      }

      if (response.status === 204) return {};

      const contentType = response.headers.get('content-type') || '';
      let payload;
      try {
        payload = contentType.includes('application/json')
          ? await response.json()
          : { message: await response.text() };
      } catch {
        throw new Error('The Shopify customer service returned an invalid response.');
      }

      if (!response.ok || payload?.ok === false) {
        throw new Error(responseErrorMessage(payload, `Request failed (${response.status}).`));
      }

      return payload;
    }

    shopifyMutation(payload, mutationName) {
      let source = payload?.body ?? payload?.response ?? payload;
      if (typeof source === 'string') {
        try { source = JSON.parse(source); }
        catch { throw new Error('The Shopify response could not be parsed.'); }
      }

      if (Array.isArray(source?.errors) && source.errors.length) {
        throw new Error(source.errors.map((error) => error.message).filter(Boolean).join(', '));
      }

      const data = source?.data ?? source;
      const result = data?.[mutationName] ?? source?.[mutationName];
      if (!result) throw new Error(`Missing ${mutationName} response.`);

      if (Array.isArray(result.userErrors) && result.userErrors.length) {
        throw new Error(result.userErrors.map((error) => error?.message || error).filter(Boolean).join(', '));
      }

      return result;
    }

    buildAddressPayload(form) {
      const data = new FormData(form);
      const payload = {
        first_name: String(data.get('firstName') || '').trim(),
        last_name: String(data.get('lastName') || '').trim(),
        company: String(data.get('company') || '').trim(),
        address1: String(data.get('address1') || '').trim(),
        address2: String(data.get('address2') || '').trim(),
        city: String(data.get('city') || '').trim(),
        province: String(data.get('province') || '').trim(),
        country: String(data.get('country') || '').trim(),
        zip: String(data.get('zip') || '').trim(),
        phone: String(data.get('phone') || '').trim(),
      };

      if (!payload.address1) throw new Error('Address line 1 is required.');
      if (!payload.city) throw new Error('City is required.');
      if (!payload.zip) throw new Error('Postal code is required.');

      return payload;
    }

    async request(resource, options = {}) {
      if (!this.apiBase) {
        throw new Error('The optional dashboard API URL has not been configured.');
      }

      const url = new URL(this.apiBase, window.location.origin);
      url.searchParams.set('resource', resource);
      const sameOrigin = url.origin === window.location.origin;
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 20000);

      let response;
      try {
        response = await fetch(url.toString(), {
          method: options.method || 'GET',
          mode: sameOrigin ? 'same-origin' : 'cors',
          credentials: options.credentials || (sameOrigin ? 'same-origin' : 'include'),
          cache: 'no-store',
          headers: {
            Accept: 'application/json',
            ...(options.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
            ...(options.headers || {}),
          },
          signal: controller.signal,
          body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
        });
      } catch (error) {
        if (error?.name === 'AbortError') {
          throw new Error('The customer account service timed out. Please try again.');
        }
        throw new Error('Unable to reach the customer account service. Please try again.');
      } finally {
        window.clearTimeout(timeout);
      }

      if (response.status === 204) return {};

      const contentType = response.headers.get('content-type') || '';
      let payload;
      try {
        payload = contentType.includes('application/json')
          ? await response.json()
          : { message: await response.text() };
      } catch {
        throw new Error('The customer account service returned an invalid response.');
      }

      if (!response.ok || payload?.ok === false) {
        throw new Error(responseErrorMessage(payload, `Request failed (${response.status}).`));
      }

      return payload?.data ?? payload;
    }

    setLoading(active) {
      const loading = this.root.querySelector('[data-page-loading]');
      if (loading) loading.hidden = !active;
    }

    async refreshFromApp() {
      this.setLoading(true);
      try {
        const remote = await this.request('bootstrap');

        // Keep the server-rendered Shopify Liquid fallback until the app backend
        // returns a populated replacement. An incomplete bootstrap response such
        // as { orders: [] } must not erase order history already present in Liquid.
        const hasRemoteOrders = Array.isArray(remote?.orders) && remote.orders.length > 0;
        const hasRemoteAddresses = Array.isArray(remote?.addresses) && remote.addresses.length > 0;

        if (Array.isArray(remote?.orders) && remote.orders.length === 0 && this.state.orders.length > 0) {
          console.warn('Bootstrap returned an empty orders array; preserving Shopify Liquid order history.');
        }

        this.state = normalizeState({
          ...this.state,
          ...remote,
          customer: {
            ...this.state.customer,
            ...(remote?.customer || {}),
          },
          orders: hasRemoteOrders ? mergeRemoteOrders(remote.orders, this.state.orders) : this.state.orders,
          addresses: hasRemoteAddresses ? remote.addresses : this.state.addresses,
          rewards: remote?.rewards ?? this.state.rewards,
          favorites: Array.isArray(remote?.favorites) ? remote.favorites : this.state.favorites,
          referral: remote?.referral ?? this.state.referral,
          reviews: remote?.reviews ?? this.state.reviews,
          preferences: {
            ...this.state.preferences,
            ...(remote?.preferences || {}),
          },
          capabilities: {
            ...this.state.capabilities,
            ...(remote?.capabilities || {}),
          },
        });
        this.renderAll();
      } catch (error) {
        console.warn('The optional dashboard API is not available; using Shopify Liquid fallback data.', error);
      } finally {
        this.setLoading(false);
      }
    }

    renderAll() {
      this.renderCustomer();
      this.renderDashboard();
      this.renderAddresses();
      this.renderOrders();
      this.renderRewards();
      this.renderFavorites();
      this.renderReferral();
      this.renderReviews();
      this.renderPreferences();
      this.renderCounts();
    }

    renderCustomer() {
      const customer = this.state.customer;
      const fullName = customer.name || compact([customer.firstName, customer.lastName]).join(' ') || 'Customer';
      this.root.querySelectorAll('[data-customer-name]').forEach((node) => { node.textContent = fullName; });
      this.root.querySelectorAll('[data-customer-email]').forEach((node) => { node.textContent = customer.email || ''; });
      this.root.querySelectorAll('[data-customer-first-name]').forEach((node) => { node.textContent = customer.firstName || fullName; });
      const location = this.state.addresses.find((address) => address.isDefault) || this.state.addresses[0];
      const locationNode = this.root.querySelector('[data-customer-location]');
      if (locationNode) locationNode.textContent = location?.city ? ` · ${location.city}${location.provinceCode ? `, ${location.provinceCode}` : ''}` : '';

      const form = this.root.querySelector('[data-profile-form]');
      if (form && !form.matches(':focus-within')) {
        form.elements.firstName.value = customer.firstName || '';
        form.elements.lastName.value = customer.lastName || '';
        form.elements.email.value = customer.email || '';
        form.elements.phone.value = customer.phone || '';
      }
    }

    renderCounts() {
      const deliveries = buildAllDeliveryOrders(this.state.orders);
      const activeDeliveries = deliveries.filter((delivery) => delivery.status.key === 'active').length;
      const values = [
        ['[data-active-order-count]', activeDeliveries],
        ['[data-dashboard-active-orders]', activeDeliveries],
        ['[data-dashboard-total-orders]', deliveries.length],
        ['[data-address-count]', this.state.addresses.length],
        ['[data-dashboard-addresses]', this.state.addresses.length],
        ['[data-favorite-count]', this.state.favorites.length],
        ['[data-review-count]', this.state.reviews.pending.length],
      ];
      values.forEach(([selector, value]) => this.root.querySelectorAll(selector).forEach((node) => { node.textContent = String(value); }));
    }

    renderDashboard() {
      const deliveries = buildAllDeliveryOrders(this.state.orders);
      const active = deliveries.filter((delivery) => delivery.status.key === 'active').slice(0, 3);
      const target = this.root.querySelector('[data-dashboard-orders]');

      if (target) {
        target.className = active.length ? 'ht2-order-mini' : '';
        target.innerHTML = active.length ? active.map((delivery) => `
          <article class="ht2-order-mini-card ht2-order-mini-card--delivery">
            <div>
              <span class="ht2-delivery-parent">${escapeHtml(delivery.orderName)}</span>
              <strong>${escapeHtml(delivery.dayLabel)}</strong>
              <small>${escapeHtml(delivery.type)} · ${escapeHtml(delivery.mainType)} · ${Number(delivery.quantity)} item${Number(delivery.quantity) === 1 ? '' : 's'}</small>
            </div>
            <div>
              <span class="ht2-status ht2-status--${escapeHtml(delivery.status.key)}">${escapeHtml(delivery.status.label)}</span>
              <button
                class="ht2-text-button"
                type="button"
                data-delivery-detail
                data-order-id="${escapeHtml(delivery.orderId)}"
                data-delivery-key="${escapeHtml(delivery.key)}"
              >View delivery</button>
            </div>
          </article>
        `).join('') : this.emptyState('No active deliveries', 'Scheduled deliveries being prepared or transported will appear here.');
      }

      const recent = deliveries.slice(0, 5);
      const recentTarget = this.root.querySelector('[data-recent-orders]');
      if (recentTarget) recentTarget.innerHTML = recent.length ? recent.map((delivery) => `
        <article class="ht2-recent-order">
          <div>
            <strong>${escapeHtml(delivery.reference)}</strong>
            <small>${escapeHtml(delivery.dayLabel)} · ${escapeHtml(delivery.type)} · ${escapeHtml(delivery.mainType)}</small>
          </div>
          <span>${escapeHtml(delivery.total || `${delivery.quantity} item${delivery.quantity === 1 ? '' : 's'}`)}</span>
          <span class="ht2-status ht2-status--${escapeHtml(delivery.status.key)}">${escapeHtml(delivery.status.label)}</span>
        </article>
      `).join('') : this.emptyState('No scheduled deliveries', 'Each delivery split from a Shopify order will appear separately.');

      const defaultAddress = this.state.addresses.find((address) => address.isDefault) || this.state.addresses[0];
      const shortAddress = this.root.querySelector('[data-default-address-short]');
      if (shortAddress) shortAddress.textContent = defaultAddress ? compact([defaultAddress.city, defaultAddress.provinceCode]).join(', ') : 'Add your first address';

      const rewards = this.state.rewards;
      this.root.querySelectorAll('[data-reward-balance]').forEach((node) => { node.textContent = rewards?.balance != null ? Number(rewards.balance).toLocaleString() : '—'; });
      const rewardSummary = this.root.querySelector('[data-reward-summary]');
      if (rewardSummary) rewardSummary.textContent = rewards ? `${rewards.tier || 'Member'} tier` : 'Connect your rewards provider';
    }

    renderAddresses() {
      const target = this.root.querySelector('[data-address-list]');
      if (!target) return;
      if (!this.state.addresses.length) {
        target.innerHTML = this.emptyState('No saved addresses', 'Add a delivery address without leaving your custom account page.', '<button class="ht2-button ht2-button--primary" type="button" data-address-create>Add address</button>');
        return;
      }
      target.innerHTML = this.state.addresses.map((address) => {
        const lines = addressLines(address);
        return `
          <article class="ht2-address-card ${address.isDefault ? 'is-default' : ''}">
            <div class="ht2-address-top">
              <span class="ht2-address-icon">⌖</span>
              ${address.isDefault ? '<span class="ht2-default-badge">Default</span>' : ''}
            </div>
            <address><strong>${escapeHtml(lines.shift() || 'Delivery address')}</strong>${lines.map((line) => `<span>${escapeHtml(line)}</span><br>`).join('')}</address>
            <div class="ht2-address-actions">
              <button class="ht2-link-button" type="button" data-address-edit="${escapeHtml(address.id)}">Edit</button>
              ${address.isDefault ? '' : `<button class="ht2-link-button" type="button" data-address-default="${escapeHtml(address.id)}">Set default</button>`}
              ${this.state.addresses.length > 1 ? `<button class="ht2-link-button ht2-link-button--danger" type="button" data-address-delete="${escapeHtml(address.id)}">Delete</button>` : ''}
            </div>
          </article>
        `;
      }).join('');
    }

    renderOrders() {
      const target = this.root.querySelector('[data-order-list]');
      if (!target) return;

      const deliveries = buildAllDeliveryOrders(this.state.orders)
        .filter((delivery) => this.orderFilter === 'all' || delivery.status.key === this.orderFilter);

      if (!deliveries.length) {
        target.innerHTML = this.emptyState('No matching deliveries', 'Scheduled deliveries matching this filter will appear here.');
        return;
      }

      target.innerHTML = deliveries.map((delivery) => this.deliveryCard(delivery)).join('');
    }

    deliveryCard(delivery) {
      const order = delivery.parentOrder;
      const canCancel = delivery.status.key === 'active' && !delivery.hasFulfillment;
      const canReview = delivery.status.key === 'fulfilled';
      const itemPreview = delivery.items.map((item) => `
        <div class="ht2-delivery-item-row">
          ${item.image ? `<img src="${escapeHtml(item.image)}" alt="" loading="lazy" width="48" height="48">` : '<span class="ht2-delivery-item-placeholder">□</span>'}
          <div>
            <strong>${escapeHtml(item.productTitle || item.title || 'Item')}</strong>
            <small>${escapeHtml(compact([item.variantTitle, item.sku ? `SKU ${item.sku}` : '']).join(' · '))}</small>
          </div>
          <span>× ${Math.max(1, Number(item.quantity || 1))}</span>
          <b>${escapeHtml(item.price || '')}</b>
        </div>
      `).join('');

      return `
        <article class="ht2-order-card ht2-delivery-order-card">
          <div class="ht2-delivery-card-head">
            <div class="ht2-delivery-reference">
              <span>Original Shopify order ${escapeHtml(delivery.orderName)}</span>
              <strong>${escapeHtml(delivery.reference)}</strong>
              <small>Delivery ${delivery.sequence} of ${delivery.deliveryCount}</small>
              <small>Ordered ${escapeHtml(formatDate(order.createdAt))}</small>
            </div>
            <span class="ht2-status ht2-status--${escapeHtml(delivery.status.key)}">${escapeHtml(delivery.status.label)}</span>
          </div>

          <div class="ht2-order-card-body">
            <div class="ht2-delivery-facts">
              <div><span>Delivery date</span><strong>${escapeHtml(delivery.dayLabel)}</strong></div>
              <div><span>Type</span><strong>${escapeHtml(delivery.type)}</strong></div>
              <div><span>_MainType</span><strong>${escapeHtml(delivery.mainType)}</strong></div>
            </div>

            ${this.deliveryProgressMarkup(delivery)}
            ${this.deliveryTrackingMarkup(delivery)}

            <div class="ht2-delivery-items">
              <div class="ht2-delivery-items-head">
                <strong>${Number(delivery.quantity)} item${Number(delivery.quantity) === 1 ? '' : 's'} in this delivery</strong>
                ${delivery.total ? `<span>${escapeHtml(delivery.total)}</span>` : ''}
              </div>
              ${itemPreview}
            </div>

            <div class="ht2-order-card-foot">
              <div class="ht2-delivery-parent-note">
                <span>Parent order</span>
                <strong>${escapeHtml(delivery.orderName)}</strong>
              </div>
              <div class="ht2-order-actions">
                <button class="ht2-small-button ht2-small-button--neutral" type="button" data-delivery-detail data-order-id="${escapeHtml(delivery.orderId)}" data-delivery-key="${escapeHtml(delivery.key)}">Complete details</button>
                ${delivery.items.some((item) => item.variantId) ? `<button class="ht2-small-button" type="button" data-delivery-reorder data-order-id="${escapeHtml(delivery.orderId)}" data-delivery-key="${escapeHtml(delivery.key)}">Reorder this delivery</button>` : ''}
                ${delivery.status.key === 'active' ? `<button class="ht2-small-button" type="button" data-delivery-edit-request data-order-id="${escapeHtml(delivery.orderId)}" data-delivery-key="${escapeHtml(delivery.key)}">Request change</button>` : ''}
                ${canCancel ? `<button class="ht2-small-button ht2-small-button--danger" type="button" data-delivery-cancel data-order-id="${escapeHtml(delivery.orderId)}" data-delivery-key="${escapeHtml(delivery.key)}">Request cancellation</button>` : ''}
                ${canReview ? `<button class="ht2-small-button" type="button" data-review-open="${escapeHtml(order.id)}">Review</button>` : ''}
              </div>
            </div>
          </div>
        </article>
      `;
    }

    deliveryTrackingMarkup(delivery, detailed = false) {
      if (!delivery.trackings.length) {
        return `
          <div class="ht2-tracking-empty">
            <span class="ht2-tracking-icon" aria-hidden="true">⌖</span>
            <div><strong>Tracking not available yet</strong><small>Carrier and tracking details will appear when this delivery is dispatched.</small></div>
          </div>
        `;
      }

      return `
        <div class="ht2-tracking-list${detailed ? ' is-detailed' : ''}">
          ${delivery.trackings.map((tracking, index) => `
            <article class="ht2-tracking-card">
              <span class="ht2-tracking-icon" aria-hidden="true">↗</span>
              <div>
                <small>${delivery.trackings.length > 1 ? `Tracking ${index + 1}` : 'Delivery tracking'}</small>
                <strong>${escapeHtml(tracking.company || 'Tracking available')}</strong>
                <span>${escapeHtml(tracking.numbers?.length > 1 ? tracking.numbers.join(', ') : (tracking.number || 'Open the carrier link for details'))}</span>
              </div>
              ${tracking.url ? `<a class="ht2-button ht2-button--secondary ht2-tracking-button" href="${escapeHtml(tracking.url)}" target="_blank" rel="noopener noreferrer">Track delivery</a>` : ''}
            </article>
          `).join('')}
        </div>
      `;
    }

    deliveryProgressMarkup(delivery) {
      if (delivery.status.key === 'cancelled') {
        return '<div class="ht2-delivery-cancelled-note">This scheduled delivery has been cancelled.</div>';
      }

      const activeIndex = delivery.status.progressIndex;
      const labels = ['Ordered', 'Confirmed', 'Preparing', 'On the way', 'Delivered'];
      return `<div class="ht2-progress">${labels.map((label, index) => {
        const className = index < activeIndex ? 'is-done' : index === activeIndex ? 'is-active' : '';
        return `<div class="ht2-progress-step ${className}"><span>${index < activeIndex ? '✓' : index + 1}</span>${escapeHtml(label)}</div>`;
      }).join('')}</div>`;
    }

    renderRewards() {
      const target = this.root.querySelector('[data-rewards-panel]');
      if (!target) return;
      const rewards = this.state.rewards;
      if (!rewards) {
        target.innerHTML = this.integrationState('Rewards are not connected yet', 'Shopify does not provide a native loyalty balance. Return reward data from your app or loyalty provider through the bootstrap API.');
        return;
      }
      const balance = Number(rewards.balance || 0);
      const nextTierAt = Number(rewards.nextTierAt || rewards.nextTierPoints || 0);
      const percentage = nextTierAt > 0 ? Math.min(100, Math.round((balance / nextTierAt) * 100)) : 100;
      target.innerHTML = `
        <section class="ht2-reward-hero">
          <div class="ht2-reward-top"><div><span class="ht2-eyebrow">Available balance</span><strong class="ht2-reward-balance">${balance.toLocaleString()} <small>pts</small></strong></div><span class="ht2-tier-badge">${escapeHtml(rewards.tier || 'Member')}</span></div>
          <div class="ht2-progress-bar"><span style="width:${percentage}%"></span></div>
          <div class="ht2-reward-meta"><span>${escapeHtml(rewards.tier || 'Current tier')}</span><span>${nextTierAt ? `${balance.toLocaleString()} / ${nextTierAt.toLocaleString()} points` : 'Top tier'}</span></div>
        </section>
        <div class="ht2-reward-grid">
          <article class="ht2-card"><span>Earned this month</span><strong>${Number(rewards.earnedThisMonth || 0).toLocaleString()}</strong><span>Points added</span></article>
          <article class="ht2-card"><span>Lifetime redeemed</span><strong>${Number(rewards.lifetimeRedeemed || 0).toLocaleString()}</strong><span>Points used</span></article>
          <article class="ht2-card"><span>Value saved</span><strong>${escapeHtml(rewards.valueSaved || '—')}</strong><span>Total reward value</span></article>
        </div>
      `;
      this.root.querySelectorAll('[data-membership-tier]').forEach((node) => { node.textContent = rewards.tier || 'Member'; });
    }

    renderFavorites() {
      const target = this.root.querySelector('[data-favorites-panel]');
      if (!target) return;
      if (!this.state.capabilities.favorites && !this.state.favorites.length) {
        target.innerHTML = this.integrationState('Favourites are ready for app data', 'Store favourite product and variant IDs in your app, then return current Shopify product data through the bootstrap API.');
        return;
      }
      if (!this.state.favorites.length) {
        target.innerHTML = this.emptyState('No favourites yet', 'Save products from the menu and they will appear here.');
        return;
      }
      target.innerHTML = this.state.favorites.map((item) => `
        <article class="ht2-product-card">
          <div class="ht2-product-media">${item.image ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.imageAlt || item.title)}" loading="lazy" width="500" height="375">` : ''}<button class="ht2-product-remove" type="button" data-favorite-remove="${escapeHtml(item.id || item.variantId || item.productId)}" aria-label="Remove ${escapeHtml(item.title)}">♥</button></div>
          <div class="ht2-product-copy"><h3>${escapeHtml(item.title)}</h3><p>${escapeHtml(item.subtitle || item.variantTitle || '')}</p><div class="ht2-product-foot"><strong>${escapeHtml(item.price || '')}</strong>${item.variantId && item.available !== false ? `<button class="ht2-small-button" type="button" data-favorite-add="${escapeHtml(item.variantId)}">Add to cart</button>` : '<span class="ht2-status ht2-status--cancelled">Unavailable</span>'}</div></div>
        </article>
      `).join('');
    }

    renderReferral() {
      const target = this.root.querySelector('[data-referral-panel]');
      if (!target) return;
      const referral = this.state.referral;
      if (!referral) {
        target.innerHTML = this.integrationState('Referral provider not connected', 'Return the authenticated customer referral code, link, totals and referral history from your provider API.');
        return;
      }
      target.innerHTML = `
        <section class="ht2-referral-hero">
          <div class="ht2-referral-grid">
            <div><span class="ht2-eyebrow">Your referral offer</span><h2>${escapeHtml(referral.heading || 'Share better meals with friends.')}</h2><p>${escapeHtml(referral.description || '')}</p><div class="ht2-referral-code"><strong>${escapeHtml(referral.code || '')}</strong><button class="ht2-button ht2-button--secondary" type="button" data-referral-copy data-referral-value="${escapeHtml(referral.link || referral.code || '')}">Copy link</button></div></div>
            <div class="ht2-referral-stats"><div><strong>${Number(referral.friendsReferred || 0).toLocaleString()}</strong><span>Friends referred</span></div><div><strong>${Number(referral.rewardsEarned || 0).toLocaleString()}</strong><span>Rewards earned</span></div><div><strong>${escapeHtml(referral.friendOffer || '—')}</strong><span>Friend offer</span></div><div><strong>${escapeHtml(referral.referrerOffer || '—')}</strong><span>Your reward</span></div></div>
          </div>
        </section>
      `;
    }

    renderReviews() {
      const target = this.root.querySelector('[data-reviews-panel]');
      if (!target) return;
      const pending = this.state.reviews.pending;
      const submitted = this.state.reviews.submitted;
      if (!this.state.capabilities.reviews && !pending.length && !submitted.length) {
        target.innerHTML = this.integrationState('Review provider not connected', 'Return pending and submitted reviews from the selected review provider, or expose provider APIs through the configured dashboard API.');
        return;
      }
      target.innerHTML = `
        <div class="ht2-card-heading"><div><span class="ht2-card-kicker">Waiting for feedback</span><h2>Pending reviews</h2></div><span class="ht2-default-badge">${pending.length}</span></div>
        <div class="ht2-review-grid">${pending.length ? pending.map((review) => `<article class="ht2-review-card"><h3>${escapeHtml(review.orderName || review.title || 'Order')}</h3><p>${escapeHtml(review.summary || `${formatDate(review.deliveredAt || review.createdAt)} · Ready to review`)}</p><button class="ht2-button ht2-button--primary" type="button" data-review-open="${escapeHtml(review.orderId)}">Write review</button></article>`).join('') : this.emptyState('Nothing waiting for review', 'Delivered orders that are eligible for review will appear here.')}</div>
        <div class="ht2-card-heading" style="margin-top:28px"><div><span class="ht2-card-kicker">Your feedback</span><h2>Submitted reviews</h2></div></div>
        <div class="ht2-review-grid">${submitted.length ? submitted.map((review) => `<article class="ht2-review-card"><div class="ht2-stars">${'★'.repeat(Math.max(0, Math.min(5, Number(review.rating || 0))))}${'☆'.repeat(Math.max(0, 5 - Number(review.rating || 0)))}</div><h3>${escapeHtml(review.orderName || review.title || 'Review')}</h3><p>${escapeHtml(review.reviewText || '')}</p><small>${escapeHtml(formatDate(review.createdAt))}</small></article>`).join('') : this.emptyState('No submitted reviews', 'Your completed reviews will be listed here.')}</div>
      `;
    }

    renderPreferences() {
      const form = this.root.querySelector('[data-preferences-form]');
      if (!form) return;
      Object.entries(this.state.preferences).forEach(([key, value]) => {
        if (form.elements[key]?.type === 'checkbox') form.elements[key].checked = Boolean(value);
      });
    }

    emptyState(title, message, action = '') {
      return `<div class="ht2-empty"><div><strong>${escapeHtml(title)}</strong><span>${escapeHtml(message)}</span>${action ? `<div style="margin-top:14px">${action}</div>` : ''}</div></div>`;
    }

    integrationState(title, message) {
      return `<div class="ht2-empty"><div><strong>${escapeHtml(title)}</strong><span>${escapeHtml(message)}</span><div style="margin-top:14px"><span class="ht2-default-badge">Provider integration required</span></div></div></div>`;
    }

    findAddress(id) { return this.state.addresses.find((address) => sameResourceId(address.id, id)); }
    findOrder(id) { return this.state.orders.find((order) => sameResourceId(order.id, id) || String(order.name || '') === String(id || '')); }

    findDelivery(orderId, deliveryKey) {
      const order = this.findOrder(orderId);
      if (!order) return null;
      const delivery = buildDeliveryOrders(order).find((item) => String(item.key) === String(deliveryKey));
      return delivery ? { order, delivery } : null;
    }

    openAddressModal(address = null) {
      const modal = this.openModal('address');
      const form = modal?.querySelector('[data-address-form]');
      if (!form) return;
      form.reset();
      modal.querySelector('[data-address-modal-title]').textContent = address ? 'Edit address' : 'Add address';
      if (address) {
        Object.entries(address).forEach(([key, value]) => {
          if (!form.elements[key]) return;
          if (form.elements[key].type === 'checkbox') form.elements[key].checked = Boolean(value);
          else form.elements[key].value = value ?? '';
        });
        form.elements.setAsDefault.checked = Boolean(address.isDefault);
      }
      this.setFormStatus(form, '');
    }

    async submitProfile(event) {
      event.preventDefault();
      const form = event.currentTarget;
      if (!form.reportValidity()) return;

      const formData = new FormData(form);
      const payload = {
        first_name: String(formData.get('firstName') || '').trim(),
        last_name: String(formData.get('lastName') || '').trim(),
        email: String(formData.get('email') || '').trim(),
        phone: String(formData.get('phone') || '').trim(),
      };

      this.setFormStatus(form, 'Saving…');
      this.toggleSubmitting(form, true);

      try {
        const response = await this.customerApi(`shopify/customer/${pathResourceId(this.customerId)}`, {
          method: 'PUT',
          body: payload,
        });
        const result = this.shopifyMutation(response, 'customerUpdate');
        if (!result.customer) throw new Error('Shopify did not return the updated customer.');

        const updated = result.customer;
        const fullName = updated.displayName || compact([updated.firstName, updated.lastName]).join(' ');
        this.state.customer = {
          ...this.state.customer,
          id: updated.id || this.state.customer.id,
          firstName: updated.firstName ?? payload.first_name,
          lastName: updated.lastName ?? payload.last_name,
          name: fullName || compact([payload.first_name, payload.last_name]).join(' '),
          email: updated.email ?? payload.email,
          phone: updated.phone ?? payload.phone,
        };

        this.renderCustomer();
        this.setFormStatus(form, 'Profile updated.', 'success');
        this.toast('Your name, email and phone were updated.');
      } catch (error) {
        this.setFormStatus(form, error.message, 'error');
      } finally {
        this.toggleSubmitting(form, false);
      }
    }

    async submitAddress(event) {
      event.preventDefault();
      const form = event.currentTarget;
      if (!form.reportValidity()) return;

      const addressId = String(form.elements.id?.value || '').trim();
      const setAsDefault = Boolean(form.elements.setAsDefault?.checked);
      let payload;
      try {
        payload = this.buildAddressPayload(form);
      } catch (error) {
        this.setFormStatus(form, error.message, 'error');
        return;
      }

      this.setFormStatus(form, 'Saving…');
      this.toggleSubmitting(form, true);

      try {
        let result;
        let savedAddress;

        if (addressId) {
          const response = await this.customerApi(`shopify/customer/address/${pathResourceId(addressId)}`, {
            method: 'PUT',
            body: { customer_id: this.customerId, ...payload },
          });
          result = this.shopifyMutation(response, 'customerAddressUpdate');
          savedAddress = normalizeAddress(result.address || result.customerAddress || payload, setAsDefault);
          savedAddress.id = result.address?.id || result.customerAddress?.id || addressId;

          this.state.addresses = this.state.addresses.map((item) => {
            if (sameResourceId(item.id, addressId)) return { ...item, ...savedAddress, isDefault: setAsDefault || item.isDefault };
            return setAsDefault ? { ...item, isDefault: false } : item;
          });
          this.toast('Address updated.');
        } else {
          const response = await this.customerApi(`shopify/customer/${pathResourceId(this.customerId)}/address`, {
            method: 'POST',
            body: payload,
          });
          result = this.shopifyMutation(response, 'customerAddressCreate');
          const createdAddress = result.address || result.customerAddress;
          if (!createdAddress) throw new Error('Shopify did not return the new address.');
          savedAddress = normalizeAddress(createdAddress, setAsDefault || this.state.addresses.length === 0);

          if (savedAddress.isDefault) {
            this.state.addresses = this.state.addresses.map((item) => ({ ...item, isDefault: false }));
          }
          this.state.addresses.push(savedAddress);
          this.toast('Address added.');
        }

        if (setAsDefault && savedAddress?.id) {
          const response = await this.customerApi(`shopify/customer/${pathResourceId(this.customerId)}/address/${pathResourceId(savedAddress.id)}/default`, {
            method: 'PUT',
          });
          this.shopifyMutation(response, 'customerUpdateDefaultAddress');
          this.state.addresses = this.state.addresses.map((item) => ({
            ...item,
            isDefault: sameResourceId(item.id, savedAddress.id),
          }));
          this.state.customer.defaultAddressId = savedAddress.id;
        }

        this.state.customer.addressesCount = this.state.addresses.length;
        this.renderAll();
        this.closeModal(form.closest('[data-modal]'));
      } catch (error) {
        this.setFormStatus(form, error.message, 'error');
      } finally {
        this.toggleSubmitting(form, false);
      }
    }

    async setDefaultAddress(id, button) {
      if (!id) return;
      if (button) button.disabled = true;
      try {
        const response = await this.customerApi(`shopify/customer/${pathResourceId(this.customerId)}/address/${pathResourceId(id)}/default`, {
          method: 'PUT',
        });
        const result = this.shopifyMutation(response, 'customerUpdateDefaultAddress');
        const defaultId = result.customer?.defaultAddress?.id || result.defaultAddress?.id || id;
        this.state.addresses = this.state.addresses.map((address) => ({
          ...address,
          isDefault: sameResourceId(address.id, defaultId),
        }));
        this.state.customer.defaultAddressId = defaultId;
        this.renderAll();
        this.toast('Default address updated.');
      } catch (error) {
        this.toast(error.message, true);
      } finally {
        if (button?.isConnected) button.disabled = false;
      }
    }

    async deleteAddress(id, button) {
      const address = this.findAddress(id);
      if (!address || !window.confirm('Delete this delivery address?')) return;
      if (button) button.disabled = true;

      try {
        const response = await this.customerApi(`shopify/customer/${pathResourceId(this.customerId)}/address/${pathResourceId(id)}`, {
          method: 'DELETE',
        });
        this.shopifyMutation(response, 'customerAddressDelete');

        const wasDefault = Boolean(address.isDefault);
        this.state.addresses = this.state.addresses.filter((item) => !sameResourceId(item.id, id));
        this.state.customer.addressesCount = this.state.addresses.length;

        if (wasDefault) {
          this.state.customer.defaultAddressId = null;
        }

        this.renderAll();
        this.toast('Address deleted.');
      } catch (error) {
        this.toast(error.message, true);
      } finally {
        if (button?.isConnected) button.disabled = false;
      }
    }

    openOrderDrawer(order, delivery) {
      if (!order || !delivery) return;
      const drawer = this.root.querySelector('[data-order-drawer]');
      const content = this.root.querySelector('[data-order-drawer-content]');
      if (!drawer || !content) return;
      this.lastFocusedElement = document.activeElement;
      drawer.hidden = false;
      document.body.style.overflow = 'hidden';
      this.root.querySelector('[data-order-drawer-title]').textContent = delivery.reference;
      content.innerHTML = this.deliveryDetailMarkup(order, delivery);
      drawer.querySelector('button')?.focus();
    }

    closeOrderDrawer() {
      const drawer = this.root.querySelector('[data-order-drawer]');
      if (!drawer) return;
      drawer.hidden = true;
      document.body.style.overflow = '';
      this.lastFocusedElement?.focus?.();
    }

    deliveryDetailMarkup(order, delivery) {
      const shipping = addressLines(order.shippingAddress || {});
      const itemTotal = delivery.total || `${delivery.quantity} item${delivery.quantity === 1 ? '' : 's'}`;

      return `<div class="ht2-order-detail ht2-delivery-detail">
        <section class="ht2-order-detail-section ht2-delivery-detail-hero">
          <div>
            <span class="ht2-eyebrow">Original order ${escapeHtml(order.name)} · Delivery ${delivery.sequence} of ${delivery.deliveryCount}</span>
            <h3>${escapeHtml(delivery.reference)}</h3><strong class="ht2-delivery-detail-date">${escapeHtml(delivery.dayLabel)}</strong>
            <p>${escapeHtml(delivery.type)} · ${escapeHtml(delivery.mainType)}</p>
          </div>
          <span class="ht2-status ht2-status--${escapeHtml(delivery.status.key)}">${escapeHtml(delivery.status.label)}</span>
        </section>

        <section class="ht2-order-detail-section">
          ${this.deliveryProgressMarkup(delivery)}
        </section>

        <section class="ht2-order-detail-section">
          <div class="ht2-card-heading"><div><span class="ht2-card-kicker">Separate shipment</span><h3>Tracking</h3></div></div>
          ${this.deliveryTrackingMarkup(delivery, true)}
        </section>

        <section class="ht2-order-detail-section">
          <div class="ht2-card-heading">
            <div><span class="ht2-card-kicker">This delivery only</span><h3>Items</h3></div>
            <span class="ht2-default-badge">${Number(delivery.quantity)} item${Number(delivery.quantity) === 1 ? '' : 's'}</span>
          </div>
          <div class="ht2-order-detail-items">${delivery.items.map((item) => this.lineItemDetailMarkup(item)).join('')}</div>
        </section>

        ${order.note ? `<section class="ht2-order-detail-section"><h3>Parent order note</h3><p class="ht2-detail-address">${escapeHtml(order.note)}</p></section>` : ''}

        <section class="ht2-order-detail-section">
          <h3>Delivery address</h3>
          <div class="ht2-detail-address">${shipping.length ? shipping.map(escapeHtml).join('<br>') : 'No shipping address available.'}</div>
        </section>

        <section class="ht2-order-detail-section">
          <h3>Payment reference</h3>
          <dl class="ht2-order-totals">
            <div><dt>This delivery's item total</dt><dd>${escapeHtml(itemTotal)}</dd></div>
            <div><dt>Original Shopify order</dt><dd>${escapeHtml(order.name || '')}</dd></div>
            <div class="is-total"><dt>Full Shopify order total</dt><dd>${escapeHtml(order.total || '')}</dd></div>
          </dl>
          <p class="ht2-payment-note">Shipping, tax and order-level discounts belong to the original Shopify order and are not duplicated across its delivery splits.</p>
        </section>
      </div>`;
    }

    lineItemDetailMarkup(item) {
      const properties = lineItemProperties(item);
      const propertyMarkup = properties.length
        ? `<dl class="ht2-line-properties">${properties.map((property) => `<div><dt>${escapeHtml(property.name)}</dt><dd>${escapeHtml(property.value)}</dd></div>`).join('')}</dl>`
        : '<p class="ht2-line-no-properties">No line-item attributes supplied.</p>';

      const productMeta = compact([
        item.vendor,
        item.productType,
        item.sku ? `SKU ${item.sku}` : '',
      ]).join(' · ');

      return `<article class="ht2-order-line-detail">
        <div class="ht2-order-line-product">
          ${item.image ? `<img src="${escapeHtml(item.image)}" alt="" loading="lazy" width="72" height="72">` : '<span class="ht2-order-line-placeholder">□</span>'}
          <div>
            <strong>${escapeHtml(item.productTitle || item.title || 'Item')}</strong>
            <small>${escapeHtml(compact([item.variantTitle, productMeta]).join(' · '))}</small>
            <span>Quantity ${Number(item.quantity || 1)}${item.unitPrice ? ` · ${escapeHtml(item.unitPrice)} each` : ''}</span>
          </div>
          <b>${escapeHtml(item.price || '')}</b>
        </div>
        ${propertyMarkup}
      </article>`;
    }

    openCancellation(order, delivery) {
      if (!order || !delivery) return;
      const modal = this.openModal('cancel-order');
      const form = modal?.querySelector('[data-cancel-form]');
      if (!form) return;

      form.elements.orderId.value = order.id;
      if (form.elements.deliveryKey) form.elements.deliveryKey.value = delivery.key;
      if (form.elements.deliveryReference) form.elements.deliveryReference.value = delivery.reference;
      if (form.elements.dayDate) form.elements.dayDate.value = delivery.dayDate;
      if (form.elements.type) form.elements.type.value = delivery.type;
      if (form.elements.mainType) form.elements.mainType.value = delivery.mainType;
      if (form.elements.lineItemIds) form.elements.lineItemIds.value = JSON.stringify(delivery.items.map((item) => item.id));
      this.setFormStatus(form, '');
    }

    async submitCancellation(event) {
      event.preventDefault();
      const form = event.currentTarget;
      this.setFormStatus(form, 'Checking eligibility…');
      this.toggleSubmitting(form, true);
      try {
        const payload = Object.fromEntries(new FormData(form));
        const data = await this.request('order_cancel', { method: 'POST', body: payload });
        const id = payload.orderId;
        let deliveryLineItemIds = [];
        try { deliveryLineItemIds = JSON.parse(payload.lineItemIds || '[]').map(String); } catch { deliveryLineItemIds = []; }

        this.state.orders = this.state.orders.map((order) => {
          if (!sameResourceId(order.id, id)) return order;
          if (data.order) return { ...order, ...data.order };

          return {
            ...order,
            lineItems: (order.lineItems || []).map((item) => (
              deliveryLineItemIds.some((lineId) => sameResourceId(lineId, item.id))
                ? { ...item, deliveryStatusOverride: 'cancelled' }
                : item
            )),
          };
        });
        this.renderAll();
        this.closeModal(form.closest('[data-modal]'));
        this.toast(data.message || 'Order cancellation submitted successfully.');
      } catch (error) {
        this.setFormStatus(form, error.message, 'error');
      } finally { this.toggleSubmitting(form, false); }
    }

    openEditRequest(order, delivery) {
      if (!order || !delivery) return;
      const modal = this.openModal('edit-request');
      const form = modal?.querySelector('[data-edit-request-form]');
      if (!form) return;

      form.elements.orderId.value = order.id;
      if (form.elements.deliveryKey) form.elements.deliveryKey.value = delivery.key;
      if (form.elements.deliveryReference) form.elements.deliveryReference.value = delivery.reference;
      if (form.elements.dayDate) form.elements.dayDate.value = delivery.dayDate;
      if (form.elements.type) form.elements.type.value = delivery.type;
      if (form.elements.mainType) form.elements.mainType.value = delivery.mainType;
      if (form.elements.lineItemIds) form.elements.lineItemIds.value = JSON.stringify(delivery.items.map((item) => item.id));
      this.setFormStatus(form, '');
    }

    async submitEditRequest(event) {
      event.preventDefault();
      const form = event.currentTarget;
      if (!form.reportValidity()) return;
      this.setFormStatus(form, 'Submitting…');
      this.toggleSubmitting(form, true);
      try {
        const payload = Object.fromEntries(new FormData(form));
        await this.request('order_edit_request', { method: 'POST', body: payload });
        this.closeModal(form.closest('[data-modal]'));
        form.reset();
        this.toast('Your order-change request has been submitted.');
      } catch (error) { this.setFormStatus(form, error.message, 'error'); }
      finally { this.toggleSubmitting(form, false); }
    }

    async reorder(order, button) {
      if (!order) return;
      const items = (order.lineItems || [])
        .map((item) => ({
          id: Number(numericResourceId(item.variantId)),
          quantity: Math.max(1, Number(item.quantity || 1)),
        }))
        .filter((item) => Number.isSafeInteger(item.id) && item.id > 0);
      if (!items.length) return this.toast('No currently available variants were found for this order.', true);
      button.disabled = true;
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.description || 'Unable to add these items to the cart.');
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: payload }));
        this.toast('Available items were added to your cart.');
      } catch (error) { this.toast(error.message, true); }
      finally { button.disabled = false; }
    }

    async addVariantToCart(variantId, quantity, button) {
      button.disabled = true;
      try {
        const numericVariantId = Number(numericResourceId(variantId));
        if (!Number.isSafeInteger(numericVariantId) || numericVariantId <= 0) {
          throw new Error('A valid Shopify variant was not supplied.');
        }
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: numericVariantId, quantity: Math.max(1, Number(quantity || 1)) }] }) });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.description || 'Unable to add this item.');
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: payload }));
        this.toast('Added to cart.');
      } catch (error) { this.toast(error.message, true); }
      finally { button.disabled = false; }
    }

    async removeFavorite(id) {
      try {
        await this.request('favorite', { method: 'POST', body: { operation: 'remove', id } });
        this.state.favorites = this.state.favorites.filter((item) => String(item.id || item.variantId || item.productId) !== String(id));
        this.renderFavorites();
        this.renderCounts();
        this.toast('Removed from favourites.');
      } catch (error) { this.toast(error.message, true); }
    }

    async copyReferral(button) {
      const value = button.dataset.referralValue;
      if (!value) return;
      try {
        await navigator.clipboard.writeText(value);
        const original = button.textContent;
        button.textContent = 'Copied';
        setTimeout(() => { button.textContent = original; }, 1600);
      } catch { this.toast('Unable to copy the referral link.', true); }
    }

    openReview(orderId) {
      const form = this.openModal('review')?.querySelector('[data-review-form]');
      if (!form) return;
      form.reset();
      form.elements.orderId.value = orderId;
      this.setRating(0);
      this.setFormStatus(form, '');
    }

    setRating(value) {
      const form = this.root.querySelector('[data-review-form]');
      if (!form) return;
      form.elements.rating.value = value || '';
      form.querySelectorAll('[data-rating-value]').forEach((button) => button.classList.toggle('is-active', Number(button.dataset.ratingValue) <= value));
    }

    async submitReview(event) {
      event.preventDefault();
      const form = event.currentTarget;
      if (!form.elements.rating.value) return this.setFormStatus(form, 'Select a star rating.', 'error');
      if (!form.reportValidity()) return;
      this.setFormStatus(form, 'Submitting…');
      this.toggleSubmitting(form, true);
      try {
        const payload = Object.fromEntries(new FormData(form));
        const data = await this.request('review', { method: 'POST', body: payload });
        if (data.reviews) this.state.reviews = data.reviews;
        else await this.refreshFromApp();
        this.closeModal(form.closest('[data-modal]'));
        this.renderReviews();
        this.renderCounts();
        this.toast('Thank you. Your review was submitted.');
      } catch (error) { this.setFormStatus(form, error.message, 'error'); }
      finally { this.toggleSubmitting(form, false); }
    }

    async submitPreferences(event) {
      event.preventDefault();
      const form = event.currentTarget;
      const payload = {};
      [...form.elements].filter((element) => element.name && element.type === 'checkbox').forEach((element) => { payload[element.name] = element.checked; });
      this.setFormStatus(form, 'Saving…');
      this.toggleSubmitting(form, true);
      try {
        const data = await this.request('preferences', { method: 'POST', body: payload });
        this.state.preferences = data.preferences || payload;
        this.setFormStatus(form, 'Settings saved.', 'success');
        this.toast('Your settings were saved.');
      } catch (error) { this.setFormStatus(form, error.message, 'error'); }
      finally { this.toggleSubmitting(form, false); }
    }

    setCurrentDate() {
      const target = this.root.querySelector('[data-current-date]');
      if (target) target.textContent = new Intl.DateTimeFormat(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }).format(new Date());
    }

    setFormStatus(form, message, type = '') {
      const status = form?.querySelector('[class*="form-status"]');
      if (!status) return;
      status.textContent = message || '';
      status.classList.toggle('is-error', type === 'error');
      status.classList.toggle('is-success', type === 'success');
    }

    toggleSubmitting(form, active) {
      form?.querySelectorAll('button, input, select, textarea').forEach((element) => {
        if (element.type === 'hidden') return;

        if (active) {
          element.dataset.ht2WasDisabled = element.disabled ? 'true' : 'false';
          element.disabled = true;
          return;
        }

        element.disabled = element.dataset.ht2WasDisabled === 'true';
        delete element.dataset.ht2WasDisabled;
      });
    }

    destroy() {
      if (this.documentKeydownHandler) {
        document.removeEventListener('keydown', this.documentKeydownHandler);
        this.documentKeydownHandler = null;
      }

      delete this.root.ht2Portal;
      delete this.root.dataset.ht2Initialized;
    }

    toast(message, error = false) {
      const region = this.root.querySelector('[data-toast-region]');
      if (!region) return;
      const toast = document.createElement('div');
      toast.className = `ht2-toast${error ? ' is-error' : ''}`;
      toast.textContent = message;
      region.appendChild(toast);
      window.setTimeout(() => toast.remove(), 4200);
    }
  }

  const init = (scope = document) => scope.querySelectorAll(SELECTOR).forEach((root) => {
    if (root.dataset.ht2Initialized === 'true') return;
    root.dataset.ht2Initialized = 'true';
    root.ht2Portal = new CustomerPortal(root);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));

  document.addEventListener('shopify:section:unload', (event) => {
    const roots = event.target.matches?.(SELECTOR)
      ? [event.target]
      : [...(event.target.querySelectorAll?.(SELECTOR) || [])];
    roots.forEach((root) => root.ht2Portal?.destroy?.());
  });

  document.addEventListener('shopify:block:select', (event) => {
    const blockElement = event.target?.closest?.('[data-block-placement]');
    const root = event.target?.closest?.(SELECTOR);
    const placement = blockElement?.dataset?.blockPlacement;
    if (!root?.ht2Portal || !placement) return;
    root.ht2Portal.showPanel(placement, false);
  });
})();
